import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';
import Dashboard from './pages/Dashboard.jsx';
import CustomerHistory from './pages/CustomerHistory.jsx';
import ActivityLog from './pages/ActivityLog.jsx';
import Finance from './pages/Finance.jsx';
import Reports from './pages/Reports.jsx';
import Login from './pages/Login.jsx';
import Customers from './pages/Customers.jsx';
import CustomerDetail from './pages/CustomerDetail.jsx';
import ChiDashboard from './pages/ChiDashboard.jsx';
import Retention from './pages/Retention.jsx';
import SiteMap from './pages/SiteMap.jsx';
import BillingSubscription from './pages/BillingSubscription.jsx';
import CustomerNew from './pages/CustomerNew.jsx';
import CustomerImport from './pages/CustomerImport.jsx';
import CustomerMailingLabel from './pages/CustomerMailingLabel.jsx';
import Leads from './pages/Leads.jsx';
import LeadDetail from './pages/LeadDetail.jsx';
import LeadNew from './pages/LeadNew.jsx';
import LeadImport from './pages/LeadImport.jsx';
import Distributors from './pages/Distributors.jsx';
import DistributorNew from './pages/DistributorNew.jsx';
import DistributorDetail from './pages/DistributorDetail.jsx';
import EmailCampaigns from './pages/EmailCampaigns.jsx';
import ImportHistory from './pages/ImportHistory.jsx';
import Sidebar from './components/Sidebar.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import Settings from './pages/Settings.jsx';
import UserManagement from './pages/UserManagement.jsx';
import Schedule from './pages/Schedule.jsx';
import InactiveSites from './pages/InactiveSites.jsx';
import OnboardingQueue from './pages/OnboardingQueue.jsx';
import BillingReportDiff from './pages/BillingReportDiff.jsx';
import Masthead from './components/Masthead.jsx';
import Tickets from './pages/Tickets.jsx';
import TicketDetail from './pages/TicketDetail.jsx';
import CredentialTemplates from './pages/CredentialTemplates.jsx';
import PortalShell from './components/PortalShell.jsx';
import PortalSites from './pages/portal/PortalSites.jsx';
import PortalMap from './pages/portal/PortalMap.jsx';
import PortalOnboarding from './pages/portal/PortalOnboarding.jsx';
import PortalHealth from './pages/portal/PortalHealth.jsx';
import PortalUsers from './pages/portal/PortalUsers.jsx';
import PortalRenewals from './pages/portal/PortalRenewals.jsx';
import PortalSupport from './pages/portal/PortalSupport.jsx';

function Shell({ children }) {
  return (
    <div className="app">
      <Sidebar />
      <div className="app-main-col">
        <Masthead />
        <main>{children}</main>
      </div>
    </div>
  );
}

function Protected({ children }) {
  return (
    <ProtectedRoute>
      <InternalOnly>
        <Shell>{children}</Shell>
      </InternalOnly>
    </ProtectedRoute>
  );
}

// Customers can never land inside the internal shell -- even by typing
// an internal URL. The server rejects their API calls anyway; this
// keeps the UX clean by bouncing them straight to the portal.
function InternalOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === 'customer') return <Navigate to="/portal" replace />;
  return children;
}

// Mirror image for /portal/* -- internal users go back to the CRM.
function PortalProtected({ children }) {
  return (
    <ProtectedRoute>
      <PortalOnly>
        <PortalShell>{children}</PortalShell>
      </PortalOnly>
    </ProtectedRoute>
  );
}

function PortalOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== 'customer') return <Navigate to="/" replace />;
  return children;
}

function ComingSoon({ title }) {
  return (
    <section className="page">
      <div className="page-header">
        <h2>{title}</h2>
      </div>
      <p className="placeholder-note">
        {title} section — to be implemented.
      </p>
    </section>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/customer-history" element={<Protected><CustomerHistory /></Protected>} />
      <Route path="/activity-log" element={<Protected><ActivityLog /></Protected>} />
      <Route path="/finance" element={<Protected><Finance /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/chi"       element={<Protected><ChiDashboard /></Protected>} />
      <Route path="/retention" element={<Protected><Retention /></Protected>} />
      <Route path="/map"       element={<Protected><SiteMap /></Protected>} />
      <Route path="/billing-subscription" element={<Protected><BillingSubscription /></Protected>} />
      <Route path="/customers/new" element={<Protected><CustomerNew /></Protected>} />
      <Route path="/customers/import" element={<Protected><CustomerImport /></Protected>} />
      <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
      {/* Mailing-label tab is wrapped in ProtectedRoute (so axios
          interceptors still see the JWT) but NOT in Shell -- we want
          a bare page that prints cleanly with no sidebar/masthead. */}
      <Route path="/customers/:id/mailing-label" element={<ProtectedRoute><CustomerMailingLabel /></ProtectedRoute>} />
      <Route path="/inactive-sites" element={<Protected><InactiveSites /></Protected>} />
      <Route path="/onboarding"     element={<Protected><OnboardingQueue /></Protected>} />
      <Route path="/billing-diff"   element={<Protected><BillingReportDiff /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/leads/new" element={<Protected><LeadNew /></Protected>} />
      <Route path="/leads/import" element={<Protected><LeadImport /></Protected>} />
      <Route path="/leads/:id" element={<Protected><LeadDetail /></Protected>} />
      <Route path="/email-campaigns" element={<Protected><EmailCampaigns /></Protected>} />
      <Route path="/schedule" element={<Protected><Schedule /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />
      <Route path="/distributors" element={<Protected><Distributors /></Protected>} />
      <Route path="/distributors/new" element={<Protected><DistributorNew /></Protected>} />
      <Route path="/distributors/:id" element={<Protected><DistributorDetail /></Protected>} />
      <Route path="/strategic-accounts" element={<Protected><ComingSoon title="Strategic Accounts" /></Protected>} />
      <Route path="/vendor-contacts" element={<Protected><ComingSoon title="Vendor Contacts" /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/settings/users" element={<Protected><UserManagement /></Protected>} />
      <Route path="/settings/credential-templates" element={<Protected><CredentialTemplates /></Protected>} />
      <Route path="/settings/imports" element={<Protected><ImportHistory /></Protected>} />
      {/* Customer portal -- external logins only (role='customer') */}
      <Route path="/portal" element={<PortalProtected><PortalSites /></PortalProtected>} />
      <Route path="/portal/map" element={<PortalProtected><PortalMap /></PortalProtected>} />
      <Route path="/portal/health" element={<PortalProtected><PortalHealth /></PortalProtected>} />
      <Route path="/portal/users" element={<PortalProtected><PortalUsers /></PortalProtected>} />
      <Route path="/portal/onboarding" element={<PortalProtected><PortalOnboarding /></PortalProtected>} />
      <Route path="/portal/renewals" element={<PortalProtected><PortalRenewals /></PortalProtected>} />
      <Route path="/portal/support" element={<PortalProtected><PortalSupport /></PortalProtected>} />
      <Route path="*" element={<Protected><Dashboard /></Protected>} />
    </Routes>
  );
}
